/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ExistingVipDiscountsRequest } from '../models/existing-vip-discounts-request';
import { ExistingVipDiscountsResponse } from '../models/existing-vip-discounts-response';
import { VipDiscountCreateRequest } from '../models/vip-discount-create-request';
import { VipDiscountCreateResponse } from '../models/vip-discount-create-response';
import { VipDiscountListByCompanyResponse } from '../models/vip-discount-list-by-company-response';
import { VipDiscountListByTypeResponse } from '../models/vip-discount-list-by-type-response';
import { VipDiscountUpdateRequest } from '../models/vip-discount-update-request';

/**
 * VIP discount resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class VipDiscountsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updateVipDiscount
   */
  static readonly UpdateVipDiscountPath = '/discounts/vip/{vipDiscountId}';

  /**
   * Updates specified VIP discount.
   *
   * Updates an existing VIP discount based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateVipDiscount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateVipDiscount$Response(
    params: {
      vipDiscountId: string;
      body: VipDiscountUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VipDiscountsService.UpdateVipDiscountPath,
      'put'
    );
    if (params) {
      rb.path('vipDiscountId', params.vipDiscountId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates specified VIP discount.
   *
   * Updates an existing VIP discount based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateVipDiscount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateVipDiscount(
    params: {
      vipDiscountId: string;
      body: VipDiscountUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateVipDiscount$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createVipDiscount
   */
  static readonly CreateVipDiscountPath = '/discounts/vip';

  /**
   * Creates a new VIP discount entry.
   *
   * Creates a new VIP discount and returns the saved resource identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createVipDiscount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createVipDiscount$Response(
    params: {
      body: VipDiscountCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<VipDiscountCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VipDiscountsService.CreateVipDiscountPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<VipDiscountCreateResponse>;
        })
      );
  }

  /**
   * Creates a new VIP discount entry.
   *
   * Creates a new VIP discount and returns the saved resource identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createVipDiscount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createVipDiscount(
    params: {
      body: VipDiscountCreateRequest;
    },
    context?: HttpContext
  ): Observable<VipDiscountCreateResponse> {
    return this.createVipDiscount$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<VipDiscountCreateResponse>) =>
          r.body as VipDiscountCreateResponse
      )
    );
  }

  /**
   * Path part for operation findExistingVipDiscountCombinations
   */
  static readonly FindExistingVipDiscountCombinationsPath =
    '/discounts/vip/existing-combinations';

  /**
   * Get already existing VIP discounts by merchant and product.
   *
   * Finds and returns already existing VIP discounts by merchant and product.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findExistingVipDiscountCombinations()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  findExistingVipDiscountCombinations$Response(
    params: {
      body: ExistingVipDiscountsRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ExistingVipDiscountsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VipDiscountsService.FindExistingVipDiscountCombinationsPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ExistingVipDiscountsResponse>;
        })
      );
  }

  /**
   * Get already existing VIP discounts by merchant and product.
   *
   * Finds and returns already existing VIP discounts by merchant and product.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `findExistingVipDiscountCombinations$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  findExistingVipDiscountCombinations(
    params: {
      body: ExistingVipDiscountsRequest;
    },
    context?: HttpContext
  ): Observable<ExistingVipDiscountsResponse> {
    return this.findExistingVipDiscountCombinations$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<ExistingVipDiscountsResponse>) =>
          r.body as ExistingVipDiscountsResponse
      )
    );
  }

  /**
   * Path part for operation getReceivedVipDiscountsByDrinkType
   */
  static readonly GetReceivedVipDiscountsByDrinkTypePath =
    '/discounts/vip/received-by-type';

  /**
   * Gets received VIP discount list.
   *
   * Finds and returns the received VIP discounts.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReceivedVipDiscountsByDrinkType()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReceivedVipDiscountsByDrinkType$Response(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<VipDiscountListByTypeResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VipDiscountsService.GetReceivedVipDiscountsByDrinkTypePath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<VipDiscountListByTypeResponse>>;
        })
      );
  }

  /**
   * Gets received VIP discount list.
   *
   * Finds and returns the received VIP discounts.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getReceivedVipDiscountsByDrinkType$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReceivedVipDiscountsByDrinkType(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<Array<VipDiscountListByTypeResponse>> {
    return this.getReceivedVipDiscountsByDrinkType$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<Array<VipDiscountListByTypeResponse>>) =>
          r.body as Array<VipDiscountListByTypeResponse>
      )
    );
  }

  /**
   * Path part for operation getReceivedVipDiscountsByCompany
   */
  static readonly GetReceivedVipDiscountsByCompanyPath =
    '/discounts/vip/received-by-company';

  /**
   * Gets received VIP discount list.
   *
   * Finds and returns the received VIP discounts.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReceivedVipDiscountsByCompany()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReceivedVipDiscountsByCompany$Response(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<VipDiscountListByCompanyResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VipDiscountsService.GetReceivedVipDiscountsByCompanyPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<
            Array<VipDiscountListByCompanyResponse>
          >;
        })
      );
  }

  /**
   * Gets received VIP discount list.
   *
   * Finds and returns the received VIP discounts.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getReceivedVipDiscountsByCompany$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReceivedVipDiscountsByCompany(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<Array<VipDiscountListByCompanyResponse>> {
    return this.getReceivedVipDiscountsByCompany$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<VipDiscountListByCompanyResponse>>) =>
          r.body as Array<VipDiscountListByCompanyResponse>
      )
    );
  }

  /**
   * Path part for operation getGivenVipDiscountLists
   */
  static readonly GetGivenVipDiscountListsPath = '/discounts/vip/given';

  /**
   * Gets given discount list.
   *
   * Finds and returns the given VIP discounts.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getGivenVipDiscountLists()` instead.
   *
   * This method doesn't expect any request body.
   */
  getGivenVipDiscountLists$Response(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<VipDiscountListByCompanyResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      VipDiscountsService.GetGivenVipDiscountListsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<
            Array<VipDiscountListByCompanyResponse>
          >;
        })
      );
  }

  /**
   * Gets given discount list.
   *
   * Finds and returns the given VIP discounts.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getGivenVipDiscountLists$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getGivenVipDiscountLists(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<Array<VipDiscountListByCompanyResponse>> {
    return this.getGivenVipDiscountLists$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<VipDiscountListByCompanyResponse>>) =>
          r.body as Array<VipDiscountListByCompanyResponse>
      )
    );
  }
}
